import type { EventReviewWarning } from '../types';

export interface EventTemporalWarningProps {
  eventTitle: string;
  warnings?: readonly EventReviewWarning[];
  /** Open stored evidence only; do not verify, refresh providers, or change values here. */
  onOpen: () => void;
  panelId?: string;
  expanded?: boolean;
  disabled?: boolean;
  className?: string;
}

/** One small control, intended to be placed directly BELOW the existing time by its owner. */
export default function EventTemporalWarning({
  eventTitle,
  warnings,
  onOpen,
  panelId,
  expanded,
  disabled = false,
  className = '',
}: EventTemporalWarningProps) {
  const dateUnverified = warnings?.includes('check-date') ?? false;
  const timeUnverified = warnings?.includes('time-unverified') ?? false;
  if (!dateUnverified && !timeUnverified) return null;

  const label = dateUnverified && timeUnverified ? 'Date/time?' : dateUnverified ? 'Date?' : 'Time?';
  const fullLabel = [
    dateUnverified ? 'Date unverified' : null,
    timeUnverified ? 'Time unverified' : null,
  ].filter(Boolean).join('; ');
  const accessibleName = `${fullLabel} for ${eventTitle || 'this event'}. Review stored source evidence.`;

  return (
    <button
      type="button"
      className={`btn rounded-pill mh-temporal-warning${className ? ` ${className}` : ''}`}
      aria-label={accessibleName}
      aria-haspopup="dialog"
      aria-controls={panelId}
      aria-expanded={expanded}
      title={accessibleName}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onOpen();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      {label}
    </button>
  );
}
